import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TenantLogoImageWithPlaceholder } from "@/components/common/TenantLogoImageWithPlaceholder";
import {
  useTenantProfile,
  useUpdateTenantProfile,
} from "@/hooks/useTenantProfile";
import { useUploadTenantLogo } from "@/hooks/useUploadTenantLogo";
import type { UpdateTenantProfileDto } from "@/types/tenant-profile";

const TENANT_LOGO_KEY = "tenant-logo";
const ALLOWED_LOGO_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

interface TenantProfileSectionProps {
  canWrite?: boolean;
}

function normalizeDescription(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export const TenantProfileSection: React.FC<TenantProfileSectionProps> = ({
  canWrite = true,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: profile, isLoading } = useTenantProfile();
  const updateProfile = useUpdateTenantProfile();
  const uploadLogo = useUploadTenantLogo();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!profile) {
      return;
    }

    setName(profile.name);
    setDescription(profile.description ?? "");
  }, [profile]);

  const profileDescription = normalizeDescription(profile?.description ?? "");
  const normalizedDescription = normalizeDescription(description);
  const trimmedName = name.trim();
  const isNameChanged = profile ? trimmedName !== profile.name : false;
  const isDescriptionChanged = profile
    ? normalizedDescription !== profileDescription
    : false;
  const isDirty = isNameChanged || isDescriptionChanged;
  const isSaving = updateProfile.isPending;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canWrite || !profile || isSaving) {
      return;
    }

    if (!trimmedName) {
      toast.error(t("billing.tenantProfileNameRequired"));
      return;
    }

    const payload: UpdateTenantProfileDto = {};
    if (isNameChanged) {
      payload.name = trimmedName;
    }
    if (isDescriptionChanged) {
      payload.description = normalizedDescription;
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        toast.success(t("billing.tenantProfileUpdateSuccess"));
      },
      onError: () => {
        toast.error(t("billing.tenantProfileUpdateError"));
      },
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canWrite) {
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      toast.error(t("billing.tenantLogoUploadError"));
      event.target.value = "";
      return;
    }

    uploadLogo.mutate(file, {
      onSuccess: () => {
        toast.success(t("billing.tenantLogoUploadSuccess"));
      },
      onError: () => {
        toast.error(t("billing.tenantLogoUploadError"));
      },
    });
    event.target.value = "";
  };

  let logoPreview: React.ReactNode = (
    <Upload className="text-muted-foreground h-10 w-10" />
  );

  if (isLoading) {
    logoPreview = (
      <span className="text-muted-foreground text-xs">
        {t("billing.loadingTenantLogoPreview")}
      </span>
    );
  } else if (profile?.hasLogo) {
    logoPreview = (
      <TenantLogoImageWithPlaceholder
        logoKey={TENANT_LOGO_KEY}
        className="h-full w-full object-contain"
        placeholder={<Upload className="text-muted-foreground h-10 w-10" />}
        alt={t("billing.tenantLogoAlt")}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-border bg-card flex flex-col gap-5 rounded-lg border p-6"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-medium">
          {t("billing.tenantProfileTitle")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("billing.tenantProfileDescription")}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <div className="flex flex-col gap-4">
          <Input
            label={t("billing.tenantProfileNameLabel")}
            value={name}
            disabled={isLoading || isSaving || !canWrite}
            onChange={(event) => setName(event.target.value)}
          />
          <Textarea
            label={t("billing.tenantProfileDescriptionLabel")}
            description={t("billing.tenantProfileDescriptionHint")}
            value={description}
            disabled={isLoading || isSaving || !canWrite}
            className="min-h-28"
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">{t("billing.tenantLogoLabel")}</p>
          <div className="border-muted-foreground/40 bg-muted/30 flex h-28 w-28 items-center justify-center overflow-hidden rounded-md border border-dashed">
            {logoPreview}
          </div>
          <input
            ref={inputRef}
            id="tenant-logo-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            disabled={uploadLogo.isPending || !canWrite}
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="new_outline"
            disabled={uploadLogo.isPending || !canWrite}
            aria-label={t("billing.uploadTenantLogo")}
            onClick={() => inputRef.current?.click()}
          >
            {uploadLogo.isPending
              ? t("billing.uploadingTenantLogo")
              : t("billing.uploadTenantLogo")}
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={
            isLoading || isSaving || !isDirty || !trimmedName || !canWrite
          }
        >
          {isSaving ? t("billing.saving") : t("billing.saveTenantProfile")}
        </Button>
      </div>
    </form>
  );
};
